import * as _ from 'lodash';
import { useCallback, useMemo } from 'react';

import { useQuery } from '../../contexts/RouterContext';
import type { OrgRange, OrgRequestParams } from '../../../shared/InternalTools/orgs';

const CONTRIBUTOR_COUNT_DEFAULT: OrgRange = { min: 0, max: 999999 };
const PROJECT_COUNT_DEFAULT: OrgRange = { min: 0, max: 10000 };

/**
 * The full, defaulted Internal Tools page state, derived from the URL query params. It holds both
 * the organization-search filters and the user-search term so the org and user cards can share a
 * single URL query. Because every write serializes the whole object, the two cards never clobber
 * each other's params.
 */
export type InternalToolsParams = {
  freeFilterEnabled: boolean;
  premiumFilterEnabled: boolean;
  premiumSeatFilterEnabled: boolean;
  premiumSeat2FilterEnabled: boolean;
  premiumComplianceFilterEnabled: boolean;
  premiumCompSecFilterEnabled: boolean;
  customBillingFilterEnabled: boolean;
  andOrToggle: boolean;
  title: string;
  contributorCountFilterEnabled: boolean;
  contributorCountRange: OrgRange;
  projectCountFilterEnabled: boolean;
  projectCountRange: OrgRange;
  selectedOrg: number | null;
  userSearch: string;
};

/**
 * The keys on {@link InternalToolsParams} whose value is a simple boolean toggle.
 */
type BooleanToggleKey = {
  [K in keyof InternalToolsParams]: InternalToolsParams[K] extends boolean ? K : never;
}[keyof InternalToolsParams];

/**
 * The boolean toggle keys serialized to the URL query. `satisfies readonly BooleanToggleKey[]`
 * ensures every entry is a real boolean key of {@link InternalToolsParams}, so the list can never
 * drift out of sync with the type.
 */
const TOGGLE_KEYS = [
  'freeFilterEnabled',
  'premiumFilterEnabled',
  'premiumSeatFilterEnabled',
  'premiumSeat2FilterEnabled',
  'premiumComplianceFilterEnabled',
  'premiumCompSecFilterEnabled',
  'customBillingFilterEnabled',
  'andOrToggle',
  'contributorCountFilterEnabled',
  'projectCountFilterEnabled',
] as const satisfies readonly BooleanToggleKey[];

export type ToggleKey = (typeof TOGGLE_KEYS)[number];

const asBool = (val: unknown): boolean => val === 'true' || val === true;

const asNum = (val: unknown, fallback: number): number => {
  if (_.isNil(val) || val === '') {
    return fallback;
  }
  const parsed = Number(val);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const asNullableNum = (val: unknown): number | null => {
  if (_.isNil(val) || val === '') {
    return null;
  }
  const parsed = Number(val);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Converts the raw URL query (all values are strings) into typed, defaulted params.
 * Defined at module scope so its identity is stable across renders, as required by `useQuery`.
 */
export function parseInternalToolsParamsFromQuery(query: Record<string, unknown>): InternalToolsParams {
  return {
    freeFilterEnabled: asBool(query.freeFilterEnabled),
    premiumFilterEnabled: asBool(query.premiumFilterEnabled),
    premiumSeatFilterEnabled: asBool(query.premiumSeatFilterEnabled),
    premiumSeat2FilterEnabled: asBool(query.premiumSeat2FilterEnabled),
    premiumComplianceFilterEnabled: asBool(query.premiumComplianceFilterEnabled),
    premiumCompSecFilterEnabled: asBool(query.premiumCompSecFilterEnabled),
    customBillingFilterEnabled: asBool(query.customBillingFilterEnabled),
    andOrToggle: asBool(query.andOrToggle),
    title: _.isString(query.title) ? query.title : '',
    contributorCountFilterEnabled: asBool(query.contributorCountFilterEnabled),
    contributorCountRange: {
      min: asNum(query.contributorCountMin, CONTRIBUTOR_COUNT_DEFAULT.min),
      max: asNum(query.contributorCountMax, CONTRIBUTOR_COUNT_DEFAULT.max),
    },
    projectCountFilterEnabled: asBool(query.projectCountFilterEnabled),
    projectCountRange: {
      min: asNum(query.projectCountMin, PROJECT_COUNT_DEFAULT.min),
      max: asNum(query.projectCountMax, PROJECT_COUNT_DEFAULT.max),
    },
    selectedOrg: asNullableNum(query.selectedOrg),
    userSearch: _.isString(query.userSearch) ? query.userSearch : '',
  };
}

/**
 * Builds the request params for `GET /api/internal-tools/orgs`. Always emits the full nested
 * shape (including ranges) regardless of whether a range filter is enabled, matching the legacy
 * `getOrgRequestParams` selector the backend expects. The user-search term is intentionally
 * excluded so typing a user email never triggers an org fetch.
 */
export function buildOrgRequestParams(params: InternalToolsParams): OrgRequestParams {
  return {
    useAnd: params.andOrToggle,
    isFree: params.freeFilterEnabled,
    isPremium: params.premiumFilterEnabled,
    isPremiumSeat: params.premiumSeatFilterEnabled,
    isPremiumSeat2: params.premiumSeat2FilterEnabled,
    isPremiumComplianceSeat: params.premiumComplianceFilterEnabled,
    isPremiumCompSecSeat: params.premiumCompSecFilterEnabled,
    isCustomBilling: params.customBillingFilterEnabled,
    title: params.title,
    contributorCount: {
      min: params.contributorCountRange.min,
      max: params.contributorCountRange.max,
      enabled: params.contributorCountFilterEnabled,
    },
    projectCount: {
      min: params.projectCountRange.min,
      max: params.projectCountRange.max,
      enabled: params.projectCountFilterEnabled,
    },
  };
}

/**
 * Serializes params back to a minimal URL query, omitting defaults so shared URLs stay clean.
 * Range bounds are persisted whenever they differ from the default, even if the range toggle
 * is off, so an admin can set a range before enabling it without losing their input.
 */
function paramsToQuery(params: InternalToolsParams): Record<string, string | number> {
  const query: Record<string, string | number> = {};

  TOGGLE_KEYS.forEach((key) => {
    if (params[key]) {
      query[key] = 'true';
    }
  });

  if (params.title) {
    query.title = params.title;
  }
  if (params.contributorCountRange.min !== CONTRIBUTOR_COUNT_DEFAULT.min) {
    query.contributorCountMin = params.contributorCountRange.min;
  }
  if (params.contributorCountRange.max !== CONTRIBUTOR_COUNT_DEFAULT.max) {
    query.contributorCountMax = params.contributorCountRange.max;
  }
  if (params.projectCountRange.min !== PROJECT_COUNT_DEFAULT.min) {
    query.projectCountMin = params.projectCountRange.min;
  }
  if (params.projectCountRange.max !== PROJECT_COUNT_DEFAULT.max) {
    query.projectCountMax = params.projectCountRange.max;
  }
  if (params.selectedOrg !== null) {
    query.selectedOrg = params.selectedOrg;
  }
  if (params.userSearch) {
    query.userSearch = params.userSearch;
  }

  return query;
}

/**
 * Keeps the Internal Tools page state in the URL query params: the org-search filters (plan
 * toggles, title, ranges, AND/OR, selected org) and the user-search term. Replaces the old
 * `internal_tooling` Redux slice. Both the org and user cards read and write through this hook,
 * so their params live in one shared query string.
 */
export function useInternalToolsParams() {
  const [params, setQuery] = useQuery<InternalToolsParams, Record<string, string | number>>(
    parseInternalToolsParamsFromQuery,
  );

  const update = useCallback((next: InternalToolsParams) => {
    setQuery(paramsToQuery(next));
  }, [setQuery]);

  /*
   * Applies an org-filter change and clears the selected org. Changing a filter can drop the
   * previously selected org from the result set, so the selection must not survive the change.
   */
  const updateFilter = useCallback((next: InternalToolsParams) => {
    update({ ...next, selectedOrg: null });
  }, [update]);

  const toggle = useCallback((key: ToggleKey) => {
    updateFilter({ ...params, [key]: !params[key] });
  }, [params, updateFilter]);

  const setText = useCallback((title: string) => {
    updateFilter({ ...params, title });
  }, [params, updateFilter]);

  const setContributorRange = useCallback((min: number, max: number) => {
    updateFilter({ ...params, contributorCountRange: { min, max } });
  }, [params, updateFilter]);

  const setProjectRange = useCallback((min: number, max: number) => {
    updateFilter({ ...params, projectCountRange: { min, max } });
  }, [params, updateFilter]);

  const setSelectedOrgId = useCallback((selectedOrg: number | null) => {
    update({ ...params, selectedOrg });
  }, [params, update]);

  const setUserSearch = useCallback((userSearch: string) => {
    update({ ...params, userSearch });
  }, [params, update]);

  const orgRequestParams = useMemo(() => buildOrgRequestParams(params), [params]);

  return {
    params,
    orgRequestParams,
    selectedOrgId: params.selectedOrg,
    userSearch: params.userSearch,
    toggle,
    setText,
    setContributorRange,
    setProjectRange,
    setSelectedOrgId,
    setUserSearch,
  };
}
